// src/api/messages.ts
const API_BASE: string = (process.env.REACT_APP_API_BASE || 'http://localhost:5000').replace(/\/$/, '');

function authHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

async function responseMessage(res: Response, fallback: string): Promise<string> {
    try {
        const data = await res.json();
        return data?.message || fallback;
    } catch {
        return fallback;
    }
}

export type MessageUser = {
    id: string;
    username: string;
    displayName: string;
    avatarUrl: string;
};

export type GroupRole = 'owner' | 'admin' | 'member' | 'presence';
export type GroupPermissionValue = 'owner' | 'admins' | 'members';
export type GroupSendPermission = 'everyone' | 'admins' | 'owner' | 'locked';
export type GroupVisibility = 'private' | 'invite_only' | 'code_request';
export type GroupType = 'normal' | 'protected' | 'authority_controlled';
export type GroupInviteRelationship = 'anyone' | 'accepted' | 'friends' | 'shared_space';
export type GroupVisibilityControl = 'members' | 'admins' | 'hidden';

export type GroupMember = MessageUser & {
    role: GroupRole;
};

export type GroupInviteEntry = {
    id: string;
    user: MessageUser;
    invitedBy?: string;
    status: 'pending' | 'accepted' | 'declined' | 'revoked';
    createdAt?: string | null;
    respondedAt?: string | null;
};

export type GroupJoinRequestEntry = {
    id: string;
    user: MessageUser;
    status: 'pending' | 'accepted' | 'declined';
    requestedAt?: string | null;
    respondedAt?: string | null;
};

export type GroupDetails = {
    id: string;
    groupName: string;
    groupAvatar: string;
    groupDescription: string;
    groupOwner: string;
    groupAdmins: string[];
    myGroupRole: GroupRole;
    presenceOwnedByPeer?: boolean;
    memberCount: number;
    memberCountHidden?: boolean;
    members: GroupMember[];
    permissions: {
        addMembers: GroupPermissionValue;
        editInfo: 'owner' | 'admins';
        sendMessages: GroupSendPermission;
    };
    settings: {
        visibility: GroupVisibility;
        groupType: GroupType;
        memberCountVisibility: GroupVisibilityControl;
        inviteRelationship: GroupInviteRelationship;
        inviteRequiresApproval: boolean;
        joinApprovalRequired: boolean;
        inviteExpiryHours: number;
        inviteCodeExpiresAt?: string | null;
        inviteCodeMaxUses: number;
        inviteCodeUses: number;
        slowModeSeconds: number;
        memberPresenceVisibility: 'normal' | 'admins' | 'hidden';
        readReceiptVisibility: 'normal' | 'admins' | 'hidden';
        typingVisibility: 'normal' | 'admins' | 'hidden';
    };
    invite: {
        code: string;
        enabled: boolean;
        regeneratedAt?: string | null;
    } | null;
    pendingInvites: GroupInviteEntry[];
    joinRequests: GroupJoinRequestEntry[];
    restrictions: {
        user: string;
        muted: boolean;
        restricted: boolean;
        reason?: string;
        until?: string | null;
    }[];
    bans?: {
        user: string;
        bannedBy: string;
        reason?: string;
        bannedAt?: string | null;
    }[];
    auditLog?: {
        actor: string;
        action: string;
        target?: string | null;
        note?: string;
        createdAt?: string | null;
    }[];
    authority?: {
        controlled: boolean;
        protected: boolean;
        lockedAt?: string | null;
        reason?: string;
    } | null;
};

export type ConversationItem = {
    id: string;
    participants: MessageUser[];
    otherUser: MessageUser | null;
    lastMessage: {
        content: string;
        sender: string;
        timestamp: string;
    } | null;
    isGroup: boolean;
    groupName: string;
    groupAvatar: string;
    groupDescription?: string;
    groupOwner?: string | null;
    groupAdmins?: string[];
    myGroupRole?: GroupRole | null;
    presenceOwnedByPeer?: boolean;
    hasLeftGroup?: boolean;
    leftAt?: string | null;
    memberCount?: number;
    memberCountHidden?: boolean;
    groupPermissions?: GroupDetails['permissions'];
    isEncrypted: boolean;
    isMuted: boolean;
    isPinned: boolean;
    isArchived?: boolean;
    isRecycled?: boolean;
    isBlocked: boolean;
    isBlockedByMe?: boolean;
    isPresenceConversation?: boolean;
    requestStatus?: 'accepted' | 'draft' | 'pending' | 'declined';
    requestedBy?: string | null;
    requestedAt?: string | null;
    preview?: {
        content: string;
        createdAt: string;
    } | null;
    unreadCount: number;
    updatedAt: string;
};

export type ChatMsg = {
    id: string;
    clientMessageId?: string;
    sender: MessageUser;
    content: string;
    messageType: 'text' | 'image' | 'file' | 'system';
    replyTo?: {
        id: string;
        senderName: string;
        content: string;
        unavailable?: boolean;
    } | null;
    forwarded?: boolean;
    readBy: { user: string; readAt: string }[];
    reactions?: { user: string; reaction: string; reactedAt: string }[];
    createdAt: string;
    editedAt?: string | null;
    deletedAt?: string | null;
};

export type SendMessageResult = ChatMsg & {
    requestStatus?: 'accepted' | 'draft' | 'pending' | 'declined';
    requestedBy?: string | null;
};

export type SearchUserResult = {
    id: string;
    username: string;
    displayName: string;
    avatarUrl: string;
};

export async function searchUsers(query: string): Promise<SearchUserResult[]> {
    if (!query || query.trim().length < 2) return [];
    const res = await fetch(`${API_BASE}/api/profile/search?q=${encodeURIComponent(query.trim())}`, {
        headers: authHeaders(),
    });
    if (!res.ok) {
        if (res.status === 429) throw new Error(await responseMessage(res, 'Too many attempts. Please wait a moment.'));
        return [];
    }
    const data = await res.json();
    return data.users || [];
}

export async function fetchConversations(): Promise<ConversationItem[]> {
    const res = await fetch(`${API_BASE}/api/messages/conversations`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`Fetch conversations failed: ${res.status}`);
    const data = await res.json();
    return data.conversations;
}

export async function fetchMessageRequests(): Promise<ConversationItem[]> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/requests`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`Fetch message requests failed: ${res.status}`);
    const data = await res.json();
    return data.requests || [];
}

export async function fetchGroupInvites(): Promise<{ conversation: ConversationItem; invite: GroupInviteEntry }[]> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/group/invites`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await responseMessage(res, `Fetch group invites failed: ${res.status}`));
    const data = await res.json();
    return data.invites || [];
}

export async function createConversation(
    participantId: string
): Promise<{ id: string; participants: MessageUser[]; isNew: boolean; requestStatus?: 'accepted' | 'draft' | 'pending' | 'declined'; requestedBy?: string | null }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ participantId }),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Create conversation failed: ${res.status}`));
    const data = await res.json();
    return data.conversation;
}

export async function respondMessageRequest(
    conversationId: string,
    action: 'accept' | 'decline'
): Promise<{ action: string; unreadCount?: number; conversation?: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/request`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ action }),
    });
    if (!res.ok) throw new Error(`Message request update failed: ${res.status}`);
    return res.json();
}

export async function createGroupConversation(
    participantIds: string[],
    groupName: string
): Promise<{ id: string; participants: MessageUser[]; groupName: string; groupOwner?: string | null; groupAdmins?: string[]; memberCount?: number; myGroupRole?: GroupRole; isNew: boolean }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ participantIds, isGroup: true, groupName }),
    });
    if (!res.ok) throw new Error(`Create group failed: ${res.status}`);
    const data = await res.json();
    return data.conversation;
}

export async function fetchGroupDetails(conversationId: string): Promise<GroupDetails> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group`, {
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Fetch group failed: ${res.status}`));
    const data = await res.json();
    return data.group;
}

export async function updateGroupDetails(
    conversationId: string,
    payload: { groupName?: string; groupAvatar?: string; groupDescription?: string }
): Promise<{ group: GroupDetails; conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Update group failed: ${res.status}`));
    return res.json();
}

export async function addGroupMembers(
    conversationId: string,
    userIds: string[],
    directAdd = false
): Promise<{ group: GroupDetails; conversation: ConversationItem; invited?: boolean }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group/members`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ userIds, directAdd }),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Add members failed: ${res.status}`));
    return res.json();
}

export async function removeGroupMember(
    conversationId: string,
    userId: string
): Promise<{ group: GroupDetails; conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group/members/${userId}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Remove member failed: ${res.status}`));
    return res.json();
}

export async function updateGroupMemberRole(
    conversationId: string,
    userId: string,
    role: 'admin' | 'member'
): Promise<{ group: GroupDetails; conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group/members/${userId}/role`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ role }),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Update member role failed: ${res.status}`));
    return res.json();
}

export async function transferGroupOwner(
    conversationId: string,
    newOwnerId: string
): Promise<{ group: GroupDetails; conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group/transfer-owner`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ newOwnerId }),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Transfer owner role failed: ${res.status}`));
    return res.json();
}

export async function leaveGroupConversation(conversationId: string): Promise<{ left: boolean; deleted?: boolean; conversation?: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group/leave`, {
        method: 'POST',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Leave group failed: ${res.status}`));
    return res.json();
}

export async function updateGroupPermissions(
    conversationId: string,
    payload: Partial<GroupDetails['permissions'] & GroupDetails['settings']>
): Promise<{ group: GroupDetails; conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group/permissions`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Update permissions failed: ${res.status}`));
    return res.json();
}

export async function updateGroupBan(
    conversationId: string,
    userId: string,
    payload: { action: 'ban' | 'unban'; reason?: string } = { action: 'ban' }
): Promise<{ group: GroupDetails; conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group/bans/${userId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Group ban update failed: ${res.status}`));
    return res.json();
}

export async function regenerateGroupInviteCode(
    conversationId: string,
    enabled = true
): Promise<{ group: GroupDetails; conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group/invite-code`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ enabled }),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Invite code update failed: ${res.status}`));
    return res.json();
}

export async function joinGroupByCode(code: string): Promise<{ status: string; conversation?: ConversationItem | null }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/group/join-by-code`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ code }),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Join by code failed: ${res.status}`));
    return res.json();
}

export async function respondGroupInvite(
    conversationId: string,
    action: 'accept' | 'decline'
): Promise<{ status: string; conversation?: ConversationItem | null }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group/invite/respond`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ action }),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Invite response failed: ${res.status}`));
    return res.json();
}

export async function reviewGroupJoinRequest(
    conversationId: string,
    userId: string,
    action: 'approve' | 'decline'
): Promise<{ group: GroupDetails; conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group/join-requests/${userId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ action }),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Join request update failed: ${res.status}`));
    return res.json();
}

export async function restrictGroupMember(
    conversationId: string,
    userId: string,
    payload: { muted?: boolean; restricted?: boolean; reason?: string; until?: string | null }
): Promise<{ group: GroupDetails; conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group/restrictions/${userId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Restriction update failed: ${res.status}`));
    return res.json();
}

export async function updateGroupAuthority(
    conversationId: string,
    payload: { controlled?: boolean; protected?: boolean; sendMessages?: GroupSendPermission; reason?: string }
): Promise<{ group: GroupDetails; conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/group/authority`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Group controls update failed: ${res.status}`));
    return res.json();
}

export type FetchMessagesResult = {
    messages: ChatMsg[];
    total: number;
    page: number;
    hasMore: boolean;
};

export async function fetchMessages(
    conversationId: string,
    params: { page?: number; limit?: number } = {}
): Promise<FetchMessagesResult> {
    const url = new URL(`${API_BASE}/api/messages/${conversationId}`);
    if (params.page) url.searchParams.set('page', String(params.page));
    if (params.limit) url.searchParams.set('limit', String(params.limit));

    const res = await fetch(url.toString(), { headers: authHeaders() });
    if (!res.ok) {
        const error = new Error(`Fetch messages failed: ${res.status}`) as Error & { status?: number };
        error.status = res.status;
        throw error;
    }
    return res.json();
}

export async function sendMessage(
    conversationId: string,
    content: string,
    messageType: string = 'text',
    clientMessageId = '',
    replyToMessageId: string | number | null = null,
    forwarded = false
): Promise<SendMessageResult> {
    const res = await fetch(`${API_BASE}/api/messages/${conversationId}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ content, messageType, clientMessageId, replyToMessageId, forwarded }),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Send message failed: ${res.status}`));
    const data = await res.json();
    return {
        ...data.message,
        requestStatus: data.requestStatus,
        requestedBy: data.requestedBy,
    };
}

export async function editMessage(messageId: string, content: string): Promise<ChatMsg> {
    const res = await fetch(`${API_BASE}/api/messages/message/${messageId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ content }),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Edit message failed: ${res.status}`));
    const data = await res.json();
    return data.message;
}

export async function markConversationRead(conversationId: string): Promise<{ unreadCount?: number }> {
    const res = await fetch(`${API_BASE}/api/messages/${conversationId}/read`, {
        method: 'PATCH',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Mark read failed: ${res.status}`);
    return res.json().catch(() => ({}));
}

export async function setMessageReaction(
    messageId: string,
    reaction: string | null
): Promise<{ messageId: string; conversationId: string; reactions: { user: string; reaction: string; reactedAt: string }[] }> {
    const res = await fetch(`${API_BASE}/api/messages/message/${messageId}/reaction`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ reaction }),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Reaction update failed: ${res.status}`));
    return res.json();
}

export async function deleteMessage(messageId: string): Promise<void> {
    const res = await fetch(`${API_BASE}/api/messages/message/${messageId}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Delete message failed: ${res.status}`));
}

export async function createSafetyReport(input: {
    reportedUserId?: string;
    conversationId?: string;
    messageId?: string | number;
    category?: 'spam' | 'harassment' | 'hate' | 'sexual' | 'violence' | 'scam' | 'other';
    note?: string;
}): Promise<{ reportId: string; message: string }> {
    const res = await fetch(`${API_BASE}/api/messages/report`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Report failed: ${res.status}`));
    return res.json();
}

export async function deleteConversation(conversationId: string): Promise<void> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Delete conversation failed: ${res.status}`));
}

export async function oblivionConversation(conversationId: string): Promise<{
    success: boolean;
    conversationId: string;
    conversationDeleted?: boolean;
    retainedForPresence?: boolean;
    removedForViewer?: boolean;
}> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/oblivion`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Oblivion failed: ${res.status}`));
    return res.json();
}

export async function archiveConversation(conversationId: string): Promise<{ conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/archive`, {
        method: 'PATCH',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Archive conversation failed: ${res.status}`));
    return res.json();
}

export async function recycleConversation(conversationId: string): Promise<{ conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/recycle`, {
        method: 'PATCH',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Recycle conversation failed: ${res.status}`));
    return res.json();
}

export async function restoreConversation(conversationId: string): Promise<{ conversation: ConversationItem }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/restore`, {
        method: 'PATCH',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Restore conversation failed: ${res.status}`));
    return res.json();
}

export async function muteConversation(conversationId: string): Promise<{ isMuted: boolean }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/mute`, {
        method: 'PATCH',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Mute conversation failed: ${res.status}`));
    return res.json();
}

export async function pinConversation(conversationId: string): Promise<{ isPinned: boolean }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/pin`, {
        method: 'PATCH',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await responseMessage(res, `Pin conversation failed: ${res.status}`));
    return res.json();
}

export async function blockConversation(conversationId: string): Promise<{ isBlocked: boolean; isBlockedByMe?: boolean; isPresenceConversation?: boolean; otherUser: { id: string; username: string; displayName: string; avatarUrl: string } | null }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/block`, {
        method: 'PATCH',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Block conversation failed: ${res.status}`);
    return res.json();
}

export async function encryptConversation(conversationId: string): Promise<{ isEncrypted: boolean }> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/encrypt`, {
        method: 'PATCH',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Encrypt conversation failed: ${res.status}`);
    return res.json();
}

export type BlockedConversation = {
    id: string;
    userId?: string | null;
    conversationId?: string | null;
    name: string;
    avatar: string;
    reason: string;
    requestStatus?: 'accepted' | 'draft' | 'pending' | 'declined';
    requestedBy?: string | null;
};

export async function fetchBlockedConversations(): Promise<BlockedConversation[]> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/blocked`, {
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Fetch blocked failed: ${res.status}`);
    const data = await res.json();
    return data.blocked;
}

export async function storeIncognitoClear(conversationId: string, messageIds: string[]): Promise<void> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/incognito-clear`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ messageIds }),
    });
    if (!res.ok) throw new Error(`Store incognito clear failed: ${res.status}`);
}

export async function fetchIncognitoPending(conversationId: string): Promise<string[]> {
    const res = await fetch(`${API_BASE}/api/messages/conversations/${conversationId}/incognito-pending`, {
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Fetch incognito pending failed: ${res.status}`);
    const data = await res.json();
    return data.messageIds || [];
}
